<template>
    <div>
        <template v-if="isLoaded">
            <div class="row p-3">
                <div
                    v-for="(alternative, alternativeIdx) in alternatives"
                    :key="alternativeIdx"
                    class="col-md-4 mb-3"
                >
                    <quotes-products-alternative-editor
                        :products="products"
                        :value="alternative"
                        :idx="alternativeIdx"
                        :next-alternative-name="nextAlternativeName"
                        :next-alternative-recommended="
                            nextAlternativeRecommended
                        "
                        :deletable="alternatives.length > 1"
                        :edit-recommended="editRecommended"
                        :default-price-locked-for-days="
                            defaultPriceLockedForDays
                        "
                        :ignore-out-of-stock="ignoreOutOfStock"
                        :ignore-deleted="ignoreDeleted"
                        :recurring-payments="recurringPayments"
                        :allow-product-discount="allowProductDiscount"
                        :default-discount-type="defaultDiscountType"
                        :quote-profile-id="quoteProfileId"
                        @create="onAlternativeCreate(alternativeIdx)"
                        @update="onAlternativeUpdate(alternativeIdx, $event)"
                        @delete="onAlternativeDelete(alternativeIdx)"
                    ></quotes-products-alternative-editor>
                </div>
                <div v-if="alternatives.length < 3" class="col-md-4 mb-3">
                    <quotes-products-alternative-editor
                        :products="products"
                        :value="null"
                        :idx="alternatives.length"
                        :next-alternative-name="nextAlternativeName"
                        :next-alternative-recommended="
                            nextAlternativeRecommended
                        "
                        :edit-recommended="editRecommended"
                        :default-price-locked-for-days="
                            defaultPriceLockedForDays
                        "
                        :ignore-out-of-stock="ignoreOutOfStock"
                        :ignore-deleted="ignoreDeleted"
                        :deletable="false"
                        :recurring-payments="recurringPayments"
                        :allow-product-discount="allowProductDiscount"
                        :default-discount-type="defaultDiscountType"
                        :quote-profile-id="quoteProfileId"
                        @create="onAlternativeCreate(alternatives.length)"
                        @update="
                            onAlternativeUpdate(alternatives.length, $event)
                        "
                        @delete="onAlternativeDelete(alternatives.length)"
                    ></quotes-products-alternative-editor>
                </div>
            </div>

            <div v-if="alternatives.length >= 3" class="row px-3">
                <p class="col-12 text-small text-right">
                    <a
                        href="#"
                        role="button"
                        @click.prevent="
                            onAlternativeCreate(alternatives.length)
                        "
                        >Lägg till ytterligare alternativ</a
                    >
                </p>
            </div>
        </template>
        <div v-else class="row">
            <div class="spinner-border text-info" role="status">
                <span class="sr-only">Laddar...</span>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped></style>

<script setup>
import { route } from "ziggy";
import { computed, onMounted, ref } from "vue";
import { downloadProducts } from "./../../products/downloadProducts";
const props = defineProps({
    id: {
        type: Number,
        default: undefined,
    },
    editRecommended: {
        type: Boolean,
        default: false,
    },
    defaultPriceLockedForDays: {
        type: Number,
        default: undefined,
    },
    ignoreOutOfStock: {
        type: Boolean,
        default: false,
    },
    ignoreDeleted: {
        type: Boolean,
        default: false,
    },
    recurringPayments: {
        type: Object,
        default: null,
    },
    allowProductDiscount: {
        type: Boolean,
        default: false,
    },
    defaultDiscountType: {
        type: String,
        default: "kr",
    },
    quoteProfileId: {
        type: Number,
        required: false,
        default: null,
    },
});
const isLoaded = ref(false);
const alternatives = ref([]);
const products = ref(undefined);
const nextAlternativeName = computed(() => {
    let nameIdx = alternatives.value.filter((a) => a.name).length + 1;
    let name = undefined;
    while (!name || alternatives.value.map((a) => a.name).includes(name)) {
        name = `Paket ${nameIdx++}`;
    }
    return name;
});
const nextAlternativeRecommended = computed(() => {
    return alternatives.value.length == 0;
});
function onAlternativeCreate(idx) {
    alternatives.value.splice(idx, 1, {});
}
function onAlternativeUpdate(idx, alternative) {
    alternatives.value.splice(idx, 1, JSON.parse(JSON.stringify(alternative)));
}
function onAlternativeDelete(idx) {
    alternatives.value.splice(idx, 1);
}
onMounted(async () => {
    if (props.recurringPayments) {
        products.value = await downloadProducts(
            false,
            props.ignoreDeleted,
            true,
            props.quoteProfileId,
            true
        );
    } else {
        products.value = await downloadProducts(
            true,
            props.ignoreDeleted,
            false,
            props.quoteProfileId,
            true
        );
    }
    if (props.id !== undefined) {
        let qpResponses = await axios.get(
            route("quote_products.indexByQuote", props.id)
        );
        alternatives.value = qpResponses.data.data.map((qp) => ({
            id: qp.id,
            name: qp.name,
            amount: qp.amount,
            recommended: qp.recommended,
            currency: qp.currency,
            discount: qp.discount,
            discount_type: qp.discount_type,
            discount_valid_until: qp.discount_valid_until,
            price_locked_until: qp.price_locked_until,
            recurring_start_date: qp.recurring_start_date,
            recurring_interval: qp.recurring_interval,
            products: qp.product_references.map((prodRef) => ({
                product: prodRef.product,
                amount: prodRef.amount,
                promptedValue: prodRef.prompted_value,
                discount: prodRef.discount ?? undefined,
                discount_type: prodRef.discount_type ?? "kr",
                is_add_on: prodRef.is_add_on ?? false,
                custom_name: prodRef.custom_name ?? undefined,
                note: prodRef.note ?? undefined,
                line_key: prodRef.line_key ?? undefined,
                seller_assigned_price:
                    prodRef.seller_assigned_price ??
                    prodRef.product.minimum_price_including_vat,
            })),
        }));
    }
    if (alternatives.value.length === 0) {
        alternatives.value.push({});
    }
    isLoaded.value = true;
});
</script>
